import { addDays, format, parseISO } from "date-fns";

/** Entries are whole days stored as `yyyy-MM-dd`; the calendar wants local midnights. */
export function parseDay(day: string): Date {
  return parseISO(`${day}T00:00:00`);
}

export function formatDay(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** The calendar's `end` is exclusive, ours is the last day. */
export function toExclusiveEnd(lastDay: string): Date {
  return addDays(parseDay(lastDay), 1);
}

export function toInclusiveEnd(exclusiveEnd: Date): string {
  return formatDay(addDays(exclusiveEnd, -1));
}

/** ISO days compare correctly as strings. */
export function rangesOverlap(
  a: { startDate: string; endDate: string },
  b: { startDate: string; endDate: string },
): boolean {
  return a.startDate <= b.endDate && b.startDate <= a.endDate;
}

/** Inclusive length in days. */
export function dayCount(range: { startDate: string; endDate: string }): number {
  return (
    Math.round(
      (parseDay(range.endDate).getTime() - parseDay(range.startDate).getTime()) / 86_400_000,
    ) + 1
  );
}

/** Day after `day`'s Friday-to-Monday weekend, for "next Monday" shortcuts. */
export function nextMonday(from: Date): Date {
  const offset = (8 - from.getDay()) % 7 || 7;

  return addDays(from, offset);
}

/**
 * A whole day moved by one working day, forward or back: « un jour plus tard » on a Friday
 * is the Monday, not the Saturday. With no working day in the week, it moves one day.
 */
export function shiftWorkingDay(day: string, step: 1 | -1, workingDays: readonly number[]): string {
  let date = addDays(parseDay(day), step);

  for (let guard = 0; guard < 7 && !workingDays.includes(date.getDay()); guard++) {
    date = addDays(date, step);
  }

  return workingDays.includes(date.getDay())
    ? formatDay(date)
    : formatDay(addDays(parseDay(day), step));
}
